/**
 * SupaFAB - Supabank Floating Action Button
 * 
 * The primary mobile navigation hub - a single floating button that expands
 * to reveal quick actions with a Liquid Glass effect. Provides centralized
 * access to key features on mobile, replacing the need for a mobile sidebar.
 * 
 * Features:
 * - 6 quick actions: AI Assistant, External Accounts, Wallet, Activity, Settings, Help
 * - Liquid Glass expansion effect with staggered animation
 * - Haptic feedback for Telegram WebApp
 * - Reduced motion support (prefers-reduced-motion)
 * - Touch targets ≥44px (Canon requirement)
 * 
 * @see CANON.md Section 5.4b — Liquid Glass System
 */

import { LivingLogoIcon } from "@/components/brand/LivingLogoIcon";
import { LivingLogoLED } from "@/components/brand/LivingLogoLED";
import { Button } from "@/components/ui/button";
import { useChatKitContext } from "@/features/assistant/context/ChatKitContext";
import { useTelegram } from "@/hooks/useTelegram";
import { useReducedMotion } from "@/lib/motion";
import { cn } from "@/lib/utils";
import { semanticTokens } from "@packages/config/theme";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowUpDown,
  Building2,
  HelpCircle,
  MessageSquare,
  Settings,
  Wallet,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

const withDark = (token: string) =>
  token
    .split(" ")
    .map((className) => `dark:${className}`)
    .join(" ");

interface SmartAction {
  id: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  action: () => void;
  color?: string;
}

export function SupaFAB() {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const fabRef = useRef<HTMLDivElement>(null);
  const reducedMotion = useReducedMotion();

  // ChatKit context for AI Assistant
  const { openChatKit } = useChatKitContext();

  // Telegram-aware: add haptic feedback
  // useTelegram provides { light, medium, heavy, success, error, warning, selection } methods
  let hapticFeedback: {
    light: () => void;
    medium: () => void;
    heavy: () => void;
    selection: () => void;
  } | null = null;
  try {
    const telegram = useTelegram();
    hapticFeedback = telegram.hapticFeedback;
  } catch {
    // Not in TelegramProvider context, skip haptic
  }

  // Define actions
  const actions: SmartAction[] = [
    {
      id: 'assistant',
      icon: MessageSquare,
      label: 'AI Assistant',
      action: () => {
        openChatKit();
      },
      color: 'from-blue-500 to-blue-600'
    },
    {
      id: 'external-accounts',
      icon: Building2,
      label: 'External Accounts',
      action: () => navigate('/dashboard/send'),
      color: 'from-cyan-500 to-cyan-600'
    },
    {
      id: 'wallet',
      icon: Wallet,
      label: 'Wallet',
      action: () => navigate('/dashboard/wallet'),
      color: 'from-emerald-500 to-emerald-600'
    },
    {
      id: 'activity',
      icon: ArrowUpDown,
      label: 'Activity',
      action: () => navigate('/dashboard/activity'),
      color: 'from-purple-500 to-purple-600'
    },
    {
      id: 'settings',
      icon: Settings,
      label: 'Settings',
      action: () => navigate('/dashboard/settings'),
      color: 'from-slate-500 to-slate-600'
    },
    {
      id: 'help',
      icon: HelpCircle,
      label: 'Help',
      action: () => navigate('/dashboard/help'),
      color: 'from-amber-500 to-amber-600'
    },
  ];

  // Toggle FAB open/close
  const toggleFab = useCallback(() => {
    hapticFeedback?.medium();
    setIsOpen(prev => !prev);
  }, [hapticFeedback]);

  // Handle action selection
  const handleAction = useCallback((action: SmartAction) => {
    hapticFeedback?.selection();
    setIsOpen(false);
    // Small delay to allow close animation
    setTimeout(() => {
      action.action();
    }, reducedMotion ? 0 : 150);
  }, [hapticFeedback, reducedMotion]);

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent | TouchEvent) => {
      if (fabRef.current && !fabRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    // Use mousedown and touchstart for better responsiveness
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: reducedMotion ? 0 : 0.05,
        delayChildren: reducedMotion ? 0 : 0.05,
      },
    },
    exit: {
      opacity: 0,
      transition: {
        staggerChildren: reducedMotion ? 0 : 0.03,
        staggerDirection: -1,
      },
    },
  };

  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20,
      scale: 0.8,
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: 'spring',
        stiffness: 400,
        damping: 25,
        duration: reducedMotion ? 0 : 0.24,
      },
    },
    exit: {
      opacity: 0,
      y: 10,
      scale: 0.9,
      transition: {
        duration: reducedMotion ? 0 : 0.12,
      },
    },
  };

  const mainButtonVariants = {
    closed: { rotate: 0 },
    open: { rotate: 45 },
  };

  return (
    <div
      ref={fabRef}
      className={cn(
        "fixed right-4 z-40 lg:hidden",
        // Position above BottomNav (bottom-24 = 96px)
        "bottom-24"
      )}
      data-testid="supa-fab"
    >
      {/* Backdrop overlay when open */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: reducedMotion ? 0 : 0.2 }}
            className={cn(
              "fixed inset-0 -z-10",
              semanticTokens.glass.subtle.background,
              semanticTokens.glass.subtle.blur,
              withDark(semanticTokens.glass.dark.surface.background),
              withDark(semanticTokens.glass.dark.surface.blur)
            )}
            aria-hidden="true"
          />
        )}
      </AnimatePresence>

      {/* Expanded action buttons */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className={cn(
              "absolute bottom-16 right-0",
              "flex flex-col-reverse items-end gap-3",
              "mb-2"
            )}
          >
            {actions.map((action) => (
              <motion.div
                key={action.id}
                variants={itemVariants}
                className="flex items-center gap-2"
              >
                {/* Label - fixed min-width for consistent appearance */}
                <motion.span
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 10 }}
                  transition={{ duration: reducedMotion ? 0 : 0.15 }}
                  className={cn(
                    "px-4 py-2 rounded-full text-sm font-medium",
                    "min-w-[140px] text-right",
                    "whitespace-nowrap",
                    semanticTokens.glass.surface.combined,
                    withDark(semanticTokens.glass.dark.surface.combined),
                    semanticTokens.fg.default
                  )}
                >
                  {action.label}
                </motion.span>

                {/* Icon button */}
                <Button
                  onClick={() => handleAction(action)}
                  className={cn(
                    // Size: 48×48px (≥44px touch target)
                    "h-12 w-12 rounded-full p-0",
                    // Liquid Glass effect (blur, border, shadow only - NO background)
                    semanticTokens.glass.elevated.blur,
                    semanticTokens.glass.elevated.border,
                    semanticTokens.glass.elevated.shadow,
                    withDark(semanticTokens.glass.dark.elevated.blur),
                    withDark(semanticTokens.glass.dark.elevated.border),
                    // Color gradient - MUST be after glass to override white background
                    `bg-gradient-to-br ${action.color}`,
                    // Text color
                    "text-white",
                    // Hover states
                    "hover:scale-105 active:scale-95",
                    // Motion
                    semanticTokens.motion.fast,
                    // Focus ring
                    semanticTokens.focus.ring,
                    // Touch
                    "touch-manipulation",
                    // Reduced motion
                    "motion-reduce:hover:scale-100 motion-reduce:active:scale-100"
                  )}
                  aria-label={action.label}
                  data-testid={`smart-fab-action-${action.id}`}
                >
                  <action.icon className="h-5 w-5" aria-hidden="true" />
                </Button>
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main FAB button */}
      <motion.div
        animate={isOpen ? 'open' : 'closed'}
        variants={mainButtonVariants}
        transition={{
          type: 'spring',
          stiffness: 500,
          damping: 30,
          duration: reducedMotion ? 0 : undefined,
        }}
        className="relative"
      >
        <Button
          onClick={toggleFab}
          className={cn(
            // Size: 64×64px (larger for better visibility and control point)
            "h-16 w-16 rounded-full p-0",
            // Enhanced gradient styling with glass effect
            "bg-gradient-to-br from-primary to-primary/90",
            "backdrop-blur-sm",
            "hover:from-primary/95 hover:to-primary/85",
            "active:scale-95",
            semanticTokens.fg.inverted,
            "border border-white/10",
            semanticTokens.motion.standard,
            // Enhanced shadow with glow effect
            "shadow-lg shadow-primary/25",
            "hover:shadow-xl hover:shadow-primary/30",
            semanticTokens.focus.ring,
            "touch-manipulation",
            "hover:scale-105",
            // Reduced motion support
            "motion-reduce:transition-none motion-reduce:hover:scale-100"
          )}
          aria-label={isOpen ? "Close quick actions" : "Open quick actions"}
          aria-expanded={isOpen}
          aria-haspopup="menu"
          data-testid="smart-fab-trigger"
        >
          {/* Always show LivingLogoIcon - Much larger for visibility and brand identity (3-4x increase) */}
          <LivingLogoIcon
            size={56}
            variant="inverted"
            showEnergyFlow={true}
            showLed={false}  // LED moved outside the button contour
          />
          <span className="sr-only">
            {isOpen ? "Close quick actions menu" : "Open quick actions menu"}
          </span>
        </Button>

        {/* LED micro-dot - Outside button contour, bottom right - Enhanced visibility */}
        {!isOpen && (
          <LivingLogoLED
            logoSize={56}
            className="z-10"
            show={true}
          />
        )}
      </motion.div>
    </div>
  );
}

export default SupaFAB;
